import React, { useState, useEffect, useCallback } from 'react';
import { 
  IonContent, IonPage, IonHeader, IonToolbar, IonBackButton, IonButtons,
  IonToast
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, Plus, Trash2, Loader2, Landmark, CreditCard } from 'lucide-react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { useAuthStore } from '../store/authStore';
import { triggerWealthSync } from '../hooks/useWealthData';

interface Transaction {
  id: string;
  date: string;
  description: string;
  category: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
}

const CATEGORIES = {
  INCOME: ['Salary', 'Business', 'Investment', 'Rental', 'Freelance', 'Other'],
  EXPENSE: ['Food', 'Transport', 'Housing', 'Utilities', 'Healthcare', 'Education', 'Entertainment', 'Shopping', 'Zakat', 'Takaful', 'Savings', 'Other']
};

const getCurrentUserId = async (user: any): Promise<string | null> => {
  if (user?.id) return user.id;
  const globalId = (window as any).__NURWALLET_USER_ID__;
  if (globalId) return globalId;
  try {
    await capacitorDbService.init();
    const guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');
    if (guestUser) {
      (window as any).__NURWALLET_USER_ID__ = guestUser.id;
      return guestUser.id;
    }
  } catch (e) {}
  try {
    const allUsers = await capacitorDbService.getAllUsers?.();
    if (allUsers && allUsers.length > 0) {
      (window as any).__NURWALLET_USER_ID__ = allUsers[0].id;
      return allUsers[0].id;
    }
  } catch (e) {}
  return null;
};

const TransactionsScreen: React.FC = () => {
  const history = useHistory();
  const { user } = useAuthStore();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filter, setFilter] = useState<'all' | 'INCOME' | 'EXPENSE'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [newTx, setNewTx] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: '',
    amount: '',
    type: 'EXPENSE' as 'INCOME' | 'EXPENSE'
  });

  // Load transactions from DB
  const loadTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = await getCurrentUserId(user);
      if (!userId) {
        setIsLoading(false);
        return;
      }
      const data = await capacitorDbService.getTransactions(userId);
      setTransactions(data.map((t: any) => ({
        id: t.id,
        date: t.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        description: t.description || t.category,
        category: t.category,
        amount: t.amount || 0,
        type: t.type as 'INCOME' | 'EXPENSE'
      })));
    } catch (error) {
      console.error('[Transactions] Load error:', error);
      showMsg('❌ Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleAdd = async () => {
    if (!newTx.description || !newTx.amount || !newTx.category) {
      showMsg('Please fill in all fields');
      return;
    }

    const userId = await getCurrentUserId(user);
    if (!userId) {
      showMsg('❌ Please login first');
      return;
    }

    try {
      await capacitorDbService.createTransaction({
        userId,
        type: newTx.type,
        category: newTx.category,
        amount: Number(newTx.amount),
        description: newTx.description,
        date: newTx.date,
        isRecurring: false
      });

      // Reset form
      setNewTx({
        date: new Date().toISOString().split('T')[0],
        description: '',
        category: '',
        amount: '',
        type: 'EXPENSE'
      });
      setShowAddForm(false);

      // Reload and sync
      await loadTransactions();
      triggerWealthSync();
      showMsg('✅ Transaction added');
    } catch (error: any) {
      console.error('Add transaction error:', error);
      showMsg('❌ Failed to add transaction');
    }
  };

  const handleDelete = async (id: string) => {
    const userId = await getCurrentUserId(user);
    if (!userId) return;
    try {
      await capacitorDbService.deleteTransaction(id, userId);
      await loadTransactions();
      triggerWealthSync();
      showMsg('✅ Transaction removed');
    } catch (error) {
      showMsg('❌ Failed to delete');
    }
  };

  const showMsg = (msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
  };

  const filtered = transactions.filter(t => filter === 'all' || t.type === filter);
  const totalIncome = transactions.filter(t => t.type === 'INCOME').reduce((sum, t) => sum + t.amount, 0);
  const totalExpense = transactions.filter(t => t.type === 'EXPENSE').reduce((sum, t) => sum + t.amount, 0);

  if (isLoading) {
    return (
      <IonPage>
        <IonContent style={{ '--background': '#f9fafb' } as React.CSSProperties}>
          <div className="flex items-center justify-center h-full">
            <Loader2 size={32} className="text-emerald-600 animate-spin" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar style={{'--background': '#f9fafb'} as React.CSSProperties}>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" text="" color="dark" />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{'--background': '#f9fafb'} as React.CSSProperties}>

        <div className="px-4 pt-2 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Transactions</h1>
          <p className="text-gray-500 mt-1">Manage your cash flow</p>
        </div>

        {/* Add Transaction Form */}
        {showAddForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4 mx-4 mt-4 animate-fade-in">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Add Transaction</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Date</label>
                <input
                  type="date"
                  value={newTx.date}
                  onChange={(e) => setNewTx({...newTx, date: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none text-gray-800"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setNewTx({...newTx, type: 'INCOME', category: ''})}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${newTx.type === 'INCOME' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  Income
                </button>
                <button
                  onClick={() => setNewTx({...newTx, type: 'EXPENSE', category: ''})}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${newTx.type === 'EXPENSE' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  Expense
                </button>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Category</label>
                <select
                  value={newTx.category}
                  onChange={(e) => setNewTx({...newTx, category: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none text-gray-800 bg-white"
                >
                  <option value="">Select category</option>
                  {CATEGORIES[newTx.type].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Description</label>
                <input
                  type="text"
                  value={newTx.description}
                  onChange={(e) => setNewTx({...newTx, description: e.target.value})}
                  placeholder="e.g., Monthly salary"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none text-gray-800"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Amount (RM)</label>
                <input
                  type="number"
                  value={newTx.amount}
                  onChange={(e) => setNewTx({...newTx, amount: e.target.value})}
                  placeholder="0.00"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none text-gray-800"
                />
              </div>
              <button
                onClick={handleAdd}
                className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                <Plus size={18} /> Add Transaction
              </button>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 px-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpRight size={16} className="text-emerald-500" />
              <span className="text-xs text-gray-500">Total Income</span>
            </div>
            <p className="text-xl font-bold text-emerald-600">RM {totalIncome.toLocaleString()}</p>
            <p className="text-xs text-gray-400">{transactions.filter(t => t.type === 'INCOME').length} transactions</p>
            <div className="mt-3 inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs">
              ▲ 12% vs last month
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownRight size={16} className="text-red-500" />
              <span className="text-xs text-gray-500">Total Expenses</span>
            </div>
            <p className="text-xl font-bold text-red-500">RM {totalExpense.toLocaleString()}</p>
            <p className="text-xs text-gray-400">{transactions.filter(t => t.type === 'EXPENSE').length} transactions</p>
            <div className="mt-3 inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs">
              ▲ 8% vs last month
            </div>
          </div>
        </div>

        {/* Net Savings */}
        <div className="px-4 mb-4">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
            <div className="flex justify-between">
              <div>
                <p className="text-gray-600">Net Savings</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">
                  RM {(totalIncome - totalExpense).toLocaleString()}
                </p>
              </div>

              <div className="text-right">
                <p className="text-xs text-gray-500">Savings Rate</p>
                <p className="font-semibold text-emerald-700">
                  {totalIncome > 0
                    ? Math.round(((totalIncome - totalExpense) / totalIncome) * 100)
                    : 0}
                  %
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Financial Management</h3>
          <p className="text-xs text-gray-500">Move between cashflow, assets and liabilities.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 px-4">
          <button
            onClick={() => history.push('/assets')}
            className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md border border-gray-100 text-left active:scale-[0.98] transition"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
              <Landmark size={18} className="text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-gray-800">Assets</p>
            <p className="text-xs text-gray-500 mt-1">Track savings, property, investments and more.</p>
          </button>

          <button
            onClick={() => history.push('/liabilities')}
            className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md border border-gray-100 text-left active:scale-[0.98] transition"
          >
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
              <CreditCard size={18} className="text-amber-600" />
            </div>
            <p className="text-sm font-semibold text-gray-800">Liabilities</p>
            <p className="text-xs text-gray-500 mt-1">Manage loans, cards and outstanding balances.</p>
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-4 px-4">
          {(['all', 'INCOME', 'EXPENSE'] as const).map(f => (
            <button 
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-3 rounded-full text-xs font-medium ${filter === f ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              {f === 'all' ? 'All' : f === 'INCOME' ? 'Income' : 'Expenses'}
            </button>
          ))}
        </div>

        {/* Transaction List */}
        <div className="space-y-3 px-4 pb-6">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                  <Plus size={28} className="text-emerald-600" />
                </div>
                <h3 className="font-bold mt-4">No Transactions Yet</h3>
                <p className="text-gray-500 mt-2">
                  Add your first transaction to start tracking your cash flow.
                </p>
              </div>
            </div>
          ) : (
            filtered.map(t => (
              <div key={t.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${t.type === 'INCOME' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    {t.type === 'INCOME' ? <ArrowUpRight size={16} className="text-emerald-600" /> : <ArrowDownRight size={16} className="text-red-600" />}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 text-sm">{t.description}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500">{t.date}</p>
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                        {t.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-bold whitespace-nowrap text-right min-w-[120px] ${t.type === 'INCOME' ? 'text-emerald-600' : 'text-red-500'}`}>
                    {t.type === 'INCOME' ? '+' : '-'}RM {t.amount.toLocaleString()}
                  </span>
                  <button onClick={() => handleDelete(t.id)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}

          <div className="bg-white rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">This Month Summary</p>
                <p className="text-gray-500 text-sm">Jun 2026</p>
              </div>

              <div className="text-right">
                <p className="text-emerald-600 font-bold text-2xl">
                  RM {(totalIncome - totalExpense).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">Net Savings</p>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowAddForm(true)}
          className="fixed right-6 w-16 h-16 rounded-full bg-emerald-600 text-white shadow-xl z-50 flex items-center justify-center"
          style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >
          <Plus size={28} />
        </button>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="top"
          color={toastMessage.includes('✅') ? 'success' : 'danger'}
        />
      </IonContent>
    </IonPage>
  );
};

export default TransactionsScreen;
